/**
 * Which CScore inputs a clinician-month is missing.
 *
 * Only inputs the score actually reads are flagged, and only when they are
 * really absent rather than legitimately empty (see src/server/lib/cscore.js):
 *
 * - Caseload: flagged only when null. It's a rounded month-to-date average,
 *   so 0 is usually real (one or two patients who left early).
 * - Attendance: the engine treats null AND 0 as "no data" and drops the
 *   metric, redistributing its weight. With zero discharges, retention also
 *   comes from attendance, so a missing attendance scores retention 0 on top.
 * - Length of stay: only read when there were discharges. A 0 or null then
 *   scores retention 0, so it's flagged only when discharges > 0.
 * - Discharges: null means the KIPU discharge report hasn't been applied to
 *   this row (the sync always writes a number, 0 included). That's flagged
 *   only once the report has landed for the month, i.e. some other row in the
 *   month has discharges. Before that it's waiting, not missing.
 *
 * Discharges, length of stay and attendance all arrive after the month closes
 * (the discharge file for August lands in September, and attendance is typed
 * in once the month is over), so for the in-progress month only caseload,
 * which updates daily, is checked.
 */

export const MISSING_FIELD_LABELS = {
  caseload: 'Caseload',
  discharges: 'Discharges',
  length_of_stay: 'Length of stay',
  attendance_pct: 'Attendance',
};

/** Null, blank or zero: the engine scores all three as "no data". */
function isEmpty(value) {
  return value == null || value === '' || Number(value) === 0;
}

/** True for the current calendar month (or later): its data isn't final yet. */
export function isOpenMonth(month, year, now = new Date()) {
  return year * 12 + month >= now.getFullYear() * 12 + now.getMonth() + 1;
}

/**
 * Fields missing from one record, in table column order.
 * `context` is { open, dischargeReportIn } for the record's month.
 */
export function missingFields(record, { open, dischargeReportIn }) {
  const missing = [];
  if (record.caseload == null || record.caseload === '') missing.push('caseload');
  if (open) return missing;

  if (record.discharges == null) {
    if (dischargeReportIn) missing.push('discharges');
  } else if (Number(record.discharges) > 0 && isEmpty(record.length_of_stay)) {
    missing.push('length_of_stay');
  }
  if (isEmpty(record.attendance_pct)) missing.push('attendance_pct');
  return missing;
}

/**
 * Every included clinician in one clinic-month with a gap.
 *
 * `records` are that month's clinician_data_records rows. `clinicians` is the
 * clinic roster ({ id, active, include_in_cscore }); a record whose clinician
 * is ignored (include_in_cscore false) or not on the roster is skipped.
 * Closed months don't check `active`: the clinic chart still averages an
 * inactive clinician's past months, so their gaps still skew it. The open
 * month only flags active clinicians. Returns [{ record_id, clinician_id, name, fields }] sorted by name.
 */
export function findMissingData(records, clinicians, { month, year, now = new Date() }) {
  const byId = new Map((clinicians || []).map(c => [c.id, c]));
  const context = {
    open: isOpenMonth(month, year, now),
    dischargeReportIn: (records || []).some(r => r.discharges != null),
  };

  const gaps = [];
  for (const record of records || []) {
    const clinician = byId.get(record.clinician_id);
    if (!clinician || clinician.include_in_cscore === false) continue;
    if (context.open && clinician.active === false) continue;

    const fields = missingFields(record, context);
    if (fields.length === 0) continue;

    gaps.push({
      record_id: record.id,
      clinician_id: record.clinician_id,
      name: record.clinician_name || record.clinicians?.full_name || clinician.full_name || '',
      fields,
    });
  }

  return gaps.sort((a, b) => a.name.localeCompare(b.name));
}
